import { Rng } from '@cloudpearl/core';

/**
 * A tiny software painter that writes RGBA into typed arrays. Textures are generated at
 * startup instead of shipped as files — no DOM canvas needed, so it runs in workers, tests,
 * and React Native (expo-gl) unchanged.
 */
export class Painter {
  readonly w: number;
  readonly h: number;
  readonly data: Float32Array;
  wrap = false;

  constructor(w: number, h: number) {
    this.w = w;
    this.h = h;
    this.data = new Float32Array(w * h * 4);
  }

  fill(r: number, g: number, b: number, a: number): this {
    for (let i = 0; i < this.data.length; i += 4) {
      this.data[i] = r;
      this.data[i + 1] = g;
      this.data[i + 2] = b;
      this.data[i + 3] = a;
    }
    return this;
  }

  /**
   * Stamp a soft brush dab. Shape: 0 = ellipse, 1 = leaf/almond (pointed ends), 2 = stroke (flat ends, tapered).
   * `mode` 'over' alpha-blends colour and accumulates alpha; 'max' keeps the max per channel (for masks).
   */
  stamp(
    cx: number,
    cy: number,
    rx: number,
    ry: number,
    angle: number,
    rgba: [number, number, number, number],
    opts: { shape?: 0 | 1 | 2; softness?: number; mode?: 'over' | 'max' | 'add'; bend?: number } = {},
  ): void {
    const shape = opts.shape ?? 0;
    const soft = opts.softness ?? 0.3;
    const mode = opts.mode ?? 'over';
    const bend = opts.bend ?? 0;
    const ca = Math.cos(angle);
    const sa = Math.sin(angle);
    const ext = Math.max(rx, ry) + 2;
    const x0 = Math.floor(cx - ext);
    const x1 = Math.ceil(cx + ext);
    const y0 = Math.floor(cy - ext);
    const y1 = Math.ceil(cy + ext);
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        let px = x;
        let py = y;
        if (this.wrap) {
          px = ((x % this.w) + this.w) % this.w;
          py = ((y % this.h) + this.h) % this.h;
        } else if (x < 0 || y < 0 || x >= this.w || y >= this.h) continue;
        const dx = x + 0.5 - cx;
        const dy = y + 0.5 - cy;
        // Local brush space: u along the long axis (ry), v across (rx).
        let u = (dx * sa + dy * ca) / ry;
        let v = (dx * ca - dy * sa) / rx;
        v -= bend * (1 - u * u);
        let dist: number;
        if (shape === 1) {
          const half = Math.max(0, 1 - u * u);
          dist = Math.max(Math.abs(u), Math.abs(v) / Math.max(half, 1e-3));
        } else if (shape === 2) {
          const taper = 1 - 0.55 * Math.max(0, u);
          dist = Math.max(Math.abs(u), Math.abs(v) / taper);
        } else {
          dist = Math.sqrt(u * u + v * v);
        }
        if (dist >= 1) continue;
        const a = rgba[3] * smoothstepF(1, 1 - soft, dist);
        if (a <= 0) continue;
        const i = (py * this.w + px) * 4;
        const d = this.data;
        if (mode === 'max') {
          d[i] = Math.max(d[i]!, rgba[0] * a);
          d[i + 1] = Math.max(d[i + 1]!, rgba[1] * a);
          d[i + 2] = Math.max(d[i + 2]!, rgba[2] * a);
          d[i + 3] = Math.max(d[i + 3]!, a);
        } else if (mode === 'add') {
          d[i] = d[i]! + rgba[0] * a;
          d[i + 1] = d[i + 1]! + rgba[1] * a;
          d[i + 2] = d[i + 2]! + rgba[2] * a;
          d[i + 3] = d[i + 3]! + a;
        } else {
          d[i] = d[i]! * (1 - a) + rgba[0] * a;
          d[i + 1] = d[i + 1]! * (1 - a) + rgba[1] * a;
          d[i + 2] = d[i + 2]! * (1 - a) + rgba[2] * a;
          d[i + 3] = d[i + 3]! + a * (1 - d[i + 3]!);
        }
      }
    }
  }

  /** Box blur one channel (wrap-aware) — softens hard dab edges into paint. */
  blurChannel(ch: number, radius: number): void {
    const { w, h, data } = this;
    const tmp = new Float32Array(w * h);
    for (let pass = 0; pass < 2; pass++) {
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          let s = 0;
          for (let k = -radius; k <= radius; k++) {
            const xx = pass === 0 ? (x + k + w) % w : x;
            const yy = pass === 1 ? (y + k + h) % h : y;
            s += data[(yy * w + xx) * 4 + ch]!;
          }
          tmp[y * w + x] = s / (radius * 2 + 1);
        }
      }
      for (let i = 0; i < w * h; i++) data[i * 4 + ch] = tmp[i]!;
    }
  }

  toUint8(): Uint8Array {
    const out = new Uint8Array(this.data.length);
    for (let i = 0; i < this.data.length; i++) out[i] = Math.max(0, Math.min(255, Math.round(this.data[i]! * 255)));
    return out;
  }
}

function smoothstepF(e0: number, e1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}

/**
 * Leaf alpha card (image 2 of the art brief): a fan of painterly leaf strokes.
 * R = per-leaf painted value (light/dark), G = per-leaf id noise, B = 1, A = coverage.
 */
export function paintLeafCard(size = 256, seed = 7): Uint8Array {
  const p = new Painter(size, size);
  p.fill(0, 0, 0, 0);
  const rng = new Rng(seed, 99);
  const c = size / 2;
  const n = 15;
  for (let i = 0; i < n; i++) {
    const ang = rng.range(0, Math.PI * 2);
    const rad = rng.range(0.05, 0.3) * size;
    const x = c + Math.cos(ang) * rad;
    const y = c + Math.sin(ang) * rad;
    const len = rng.range(0.11, 0.2) * size;
    const wid = len * rng.range(0.32, 0.5);
    const dir = ang + rng.range(-0.6, 0.6) + Math.PI / 2;
    const value = rng.range(0.25, 1);
    p.stamp(x, y, wid, len, dir, [value, rng.float(), 1, 1], { shape: 1, softness: 0.12, bend: rng.range(-0.25, 0.25) });
    // a lighter vein-side highlight on some leaves
    if (rng.chance(0.5)) {
      p.stamp(x, y, wid * 0.45, len * 0.8, dir, [Math.min(1, value + 0.35), rng.float(), 1, 0.7], { shape: 1, softness: 0.4 });
    }
  }
  // Clamp alpha and keep the card's border transparent.
  const d = p.data;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = (y * size + x) * 4;
      const ex = Math.min(x, size - 1 - x, y, size - 1 - y);
      if (ex < 2) d[i + 3] = 0;
      d[i + 3] = Math.min(1, d[i + 3]!);
    }
  }
  return p.toUint8();
}

/**
 * Tiling painterly detail texture.
 * R = scattered dabs, G = vertical strokes (bark/cliffs), B = fine grain, A = large blotches.
 */
export function paintBrushTexture(size = 256, seed = 3): Uint8Array {
  const p = new Painter(size, size);
  p.wrap = true;
  p.fill(0.5, 0.5, 0.5, 0.5);
  const rng = new Rng(seed, 77);
  // R: dabs in random directions
  for (let i = 0; i < 900; i++) {
    const v = rng.range(0.15, 0.95);
    p.stamp(rng.float() * size, rng.float() * size, rng.range(2, 5), rng.range(6, 14), rng.range(0, Math.PI), [v, 0, 0, 0.75], { shape: 2, softness: 0.5 });
  }
  // G: long vertical strokes
  const g = new Painter(size, size);
  g.wrap = true;
  g.fill(0.5, 0.5, 0.5, 1);
  for (let i = 0; i < 520; i++) {
    const v = rng.range(0.1, 1);
    g.stamp(rng.float() * size, rng.float() * size, rng.range(1.5, 3.5), rng.range(14, 34), rng.range(-0.12, 0.12), [v, v, v, 0.8], { shape: 2, softness: 0.45 });
  }
  // A: big soft blotches
  const a = new Painter(size, size);
  a.wrap = true;
  a.fill(0.5, 0.5, 0.5, 1);
  for (let i = 0; i < 70; i++) {
    const v = rng.range(0.05, 1);
    a.stamp(rng.float() * size, rng.float() * size, rng.range(14, 34), rng.range(18, 46), rng.range(0, Math.PI), [v, v, v, 0.6], { shape: 0, softness: 0.8 });
  }
  a.blurChannel(0, 3);
  const d = p.data;
  for (let i = 0; i < size * size; i++) {
    d[i * 4 + 1] = g.data[i * 4]!;
    d[i * 4 + 2] = 0.35 + rng.float() * 0.3 + (rng.float() - 0.5) * 0.2;
    d[i * 4 + 3] = a.data[i * 4]!;
  }
  p.blurChannel(2, 1);
  return p.toUint8();
}
